/**
 * Minimal hash-based router for the web app.
 *
 * Four routes today:
 *   - `#/` (or no hash) → main inbox (MainPage)
 *   - `#/c/:conversationKey` → MainPage with the SW's deep-link key
 *     (the SW `notificationclick` handler sets this URL when the
 *     user taps the OS notification body; MainPage reads the key and
 *     scrolls to + highlights the thread)
 *   - `#/settings` → SettingsPage (theme picker + disconnect)
 *   - `/uninstall` → UninstallPage. A REAL path, not a hash route —
 *     see `isUninstallPath` below for why this one is the exception.
 *     The only route reachable by someone with no session: Chrome
 *     opens it when the extension is removed
 *     (`chrome.runtime.setUninstallURL`). `App` resolves it BEFORE
 *     the paired gate for exactly that reason. `#/uninstall` is
 *     still honoured so links minted before the path survive.
 *
 * Hash-based instead of `History API` so the SW can cheap-and-cheerfully
 * push a focused tab to a new URL via `client.navigate("/#/c/foo")`
 * without needing a history stack manipulation that would round-trip
 * through the page React tree first. Hash routing also avoids the
 * server having to know about `/c/:key` when the static host serves
 * `index.html` for unknown paths.
 *
 * Conversation key encoding: the key is `packageName|id|tag` with `|`
 * separators (per `apps/extension/src/storage/conversations.ts` /
 * `@reflecto/client-core`). `encodeURIComponent` round-trips them
 * losslessly through the hash.
 *
 * No router dependency — `react-router` would pull ~16 KB minified
 * for three routes that need ~30 lines of code. If a third surface
 * (Electron?) ever needs the same scheme, lift this into a shared
 * client-core helper at that point.
 */

import type { PairIntent } from "@reflecto/core";
import { useEffect, useState } from "react";

export type Route =
  | { name: "main" }
  | { name: "conversation"; conversationKey: string }
  | { name: "settings" }
  | { name: "uninstall" };

export function parseHash(hash: string): Route {
  // `location.hash` includes the leading `#`; strip it and treat
  // empty / `/` as the default route.
  const path = hash.replace(/^#/, "");
  if (path === "" || path === "/") return { name: "main" };
  if (path === "/settings") return { name: "settings" };
  if (path === "/uninstall") return { name: "uninstall" };
  // `/c/:key` — the key portion may itself contain encoded `|`
  // separators (`%7C`); `decodeURIComponent` reverses the
  // navigateToConversation encode.
  const conversationMatch = path.match(/^\/c\/(.+)$/);
  if (conversationMatch?.[1]) {
    try {
      return { name: "conversation", conversationKey: decodeURIComponent(conversationMatch[1]) };
    } catch {
      // Malformed escape — fall through to main.
    }
  }
  return { name: "main" };
}

/**
 * Build the hash URL for a conversation deep-link. Used by the SW
 * (`apps/web/src/sw/index.ts` notificationclick handler) and any
 * future in-app deep link.
 */
export function conversationHash(conversationKey: string): string {
  return `#/c/${encodeURIComponent(conversationKey)}`;
}

/**
 * React hook returning the current parsed route, re-rendering on
 * `hashchange`. Drives the App-level switch between PairingPage /
 * MainPage / SettingsPage.
 */
export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    typeof window === "undefined" ? { name: "main" } : parseHash(window.location.hash)
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

/**
 * Web Share Target landing path.
 *
 * The PWA manifest's `share_target.action` is the RELATIVE URL
 * `"share-target"`, which the UA resolves against the manifest's own
 * URL — so it lands at `${BASE_URL}share-target` (`/share-target` in
 * production, `/pr-123/share-target` on a per-PR preview). This is a
 * REAL path navigation (not a hash route): the OS share sheet opens the
 * PWA at that URL with `?title=&text=&url=` query params, the static
 * host serves `index.html` for it (same SPA-fallback the conversation
 * deep-links rely on), and `App` detects it here before the hash router.
 *
 * `import.meta.env.BASE_URL` is Vite's build-time-resolved `base`
 * (always ends with `/`), so this comparison is VITE_BASE_PATH-aware
 * without hardcoding `/`.
 */
export function isShareTargetPath(pathname: string = window.location.pathname): boolean {
  const base = import.meta.env.BASE_URL; // e.g. "/" or "/pr-123/"
  return pathname === `${base}share-target` || pathname === `${base}share-target/`;
}

/**
 * Post-uninstall page, as a REAL path (`/uninstall`) rather than a hash
 * route — the second and last exception to this app's hash routing,
 * alongside Share Target above.
 *
 * Worth the exception because this is the one URL a user reads cold, in
 * their own address bar, at the moment they are leaving: Chrome opens it
 * on extension removal (`chrome.runtime.setUninstallURL`). `#/uninstall`
 * works identically but leaks an implementation detail into the only
 * URL this product ever asks a departing person to look at.
 *
 * Safe on both deploy targets: `deploy.yml` and `pr-preview.yml` each
 * copy `index.html` → `404.html`, which is how GitHub Pages serves the
 * app shell for a non-root path (`pr-preview` does it per preview
 * directory, since Pages resolves `<dir>/404.html`, not the root's).
 * That is the same fallback Share Target already depends on.
 *
 * `#/uninstall` is deliberately still honoured by `parseHash` — it costs
 * one line, and it keeps any link minted before this change alive.
 */
export function isUninstallPath(pathname: string = window.location.pathname): boolean {
  const base = import.meta.env.BASE_URL; // e.g. "/" or "/pr-123/"
  return pathname === `${base}uninstall` || pathname === `${base}uninstall/`;
}

/**
 * Pull the shared payload off the Share Target navigation URL. The
 * manifest declares `text`, `title`, and `url` params; we prefer
 * `text`, fall back to `url`, then `title`, joining what's present so a
 * "share a link" intent (URL only) still produces sendable content.
 */
export function readSharedText(search: string = window.location.search): string {
  const params = new URLSearchParams(search);
  const text = params.get("text") ?? "";
  const url = params.get("url") ?? "";
  const title = params.get("title") ?? "";
  // Most shares populate `text`; link shares populate `url`. Combine
  // non-empty parts, deduped, so neither flavour loses content and a
  // link share that fills both `text` and `url` with the same URL
  // doesn't yield "url\nurl".
  const parts = [...new Set([text, url])].filter((p) => p.length > 0);
  // `Array.join` ignores the separator for a single element, so a
  // plain "\n" join handles both the one-part and two-part cases.
  const joined = parts.join("\n");
  return joined || title;
}

/**
 * Read the pairing intent off the pairing page's URL (spec O2, amended
 * 2026-08-07 — the web assistant TOGGLE was removed in favor of this).
 * `?intent=assistant` routes the minted code to the agents-only flow;
 * anything else — absent, malformed, or an unrecognised value — falls
 * back to `undefined`, matching `initPairing`'s own default-omit
 * convention for the mirror flow.
 *
 * Fail-safe direction is deliberate, not incidental: a missed
 * `assistant` tag costs the user one skippable NLS permission prompt
 * (O3), while a wrongly-tagged `mirror` user is stranded in the
 * assistant flow's connectivity-free wizard (O4, the #961 dead end).
 * So this only ever returns `"assistant"` on an exact match — never on
 * a fuzzy or case-insensitive one.
 */
export function parsePairingIntent(
  search: string = window.location.search
): PairIntent | undefined {
  const params = new URLSearchParams(search);
  return params.get("intent") === "assistant" ? "assistant" : undefined;
}

/** Imperative navigation — sets `location.hash` and triggers `hashchange`. */
export function navigate(route: Route): void {
  if (typeof window === "undefined") return;
  switch (route.name) {
    case "main":
      window.location.hash = "/";
      break;
    case "settings":
      window.location.hash = "/settings";
      break;
    case "uninstall":
      window.location.hash = "/uninstall";
      break;
    case "conversation":
      window.location.hash = `/c/${encodeURIComponent(route.conversationKey)}`;
      break;
  }
}
